<template>
  <div class="flex">
    <!-- Table Section -->
    <div :class="['overflow-x-auto transition-all', { 'w-full': !showPanel, 'w-2/3': showPanel }]">
      <table class="min-w-full bg-white border border-gray-200 rounded-md shadow">
        <thead>
          <tr class="bg-gray-100">
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Name</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Professional Body</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Registration Number</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Specialty</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Experience (Years)</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Payment Status</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Verification</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="locum in locums" :key="locum.id" class="border-t">
            <td class="py-3 px-4">{{ locum.user?.name || 'N/A' }}</td>
            <td class="py-3 px-4">{{ locum.professional_body?.name || 'N/A' }}</td>
            <td class="py-3 px-4">{{ locum.professional_registration_number || 'N/A' }}</td>
            <td class="py-3 px-4">{{ locum.primary_role_specialty || 'N/A' }}</td>
            <td class="py-3 px-4">{{ locum.work_experience_years || 'N/A' }}</td>
            <td class="py-3 px-4">
              <span :class="{
                'px-4 py-1 rounded-md text-text-lg': true,
                'bg-red-200 text-red-800': locum.status === 'pending',
                'bg-green-200 text-green-800': locum.status === 'paid',
              }">
                {{ locum.status === 'paid' ? 'Paid' : 'Pending' }}
              </span>
            </td>
            <td class="py-3 px-4">
              <span :class="{
                'px-4 py-1 rounded-md text-text-lg': true,
                'bg-yellow-200 text-yellow-800': locum.verification === 'unverified',
                'bg-blue-200 text-blue-800': locum.verification === 'verified',
              }">
                {{ locum.verification === 'verified' ? 'Verified' : 'Unverified' }}
              </span>
            </td>
            <td class="py-3 px-4">
              <button @click="openPanel(locum)"
                class="bg-orange-400 text-white px-4 py-2 rounded hover:bg-orange-600">
                View Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="flex justify-center items-center my-8">
        <div class="flex space-x-2">
          <!-- Previous Button -->
          <button 
            @click="changePage(currentPage - 1)" 
            :disabled="currentPage === 1"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <!-- Page Numbers -->
          <button 
            v-for="page in totalPages" 
            :key="page"
            @click="changePage(page)"
            :class="{ 
              'bg-blue-900 text-white': currentPage === page, 
              'text-gray-600': currentPage !== page 
            }"
            class="px-3 py-1 rounded hover:bg-blue-100"
          >
            {{ page }}
          </button>

          <!-- Next Button -->
          <button 
            @click="changePage(currentPage + 1)" 
            :disabled="currentPage === totalPages"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Right Side Panel -->
    <div v-if="showPanel"
      class="w-1/3 bg-white px-2 border-l border-gray-200 transition-all flex flex-col max-h-[74vh]">
      <div class="flex justify-between items-center p-3 bg-gray-100 border-b border-gray-200">
        <span :class="{
          'px-4 py-1 rounded-md text-text-lg': true,
          'bg-yellow-200 text-yellow-800': selectedLocum?.verification === 'unverified',
          'bg-blue-200 text-blue-800': selectedLocum?.verification === 'verified',
        }">
          {{ selectedLocum?.verification === 'verified' ? 'Verified' : 'Unverified' }}
        </span>
        <div class="flex items-center">
          <button @click="closePanel" class="text-gray-500 hover:text-gray-700 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div class="overflow-y-auto flex-grow">
        <div class="p-4">
          <h2 class="text-xl font-bold mb-4">User Information</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm">Name:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.user?.name || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Email:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.user?.email || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Phone:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.user?.phone || 'N/A' }}</div>
            </div>
          </div>

          <h2 class="text-xl font-bold mb-4">Professional Information</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm">Professional Body:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.professional_body?.name || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Reg Number:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.professional_registration_number || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Specialty:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.primary_role_specialty || 'N/A' }}</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Work Experience:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.work_experience_years || 'N/A' }} years</div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Current Hospital:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedLocum?.current_hospital_of_service || 'N/A' }}</div>
            </div>
          </div>

          <!-- Documents Section -->
          <h2 class="text-xl font-bold mb-4">Documents</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">Professional Document:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">
                <a v-if="selectedLocum?.professional_documents_url" :href="selectedLocum.professional_documents_url"
                  target="_blank" class="text-blue-500 hover:underline">
                  View Document
                </a>
                <span v-else class="text-gray-500">No Document</span>
              </div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm mb-1">National ID Document:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">
                <a v-if="selectedLocum?.national_id_document_url" :href="selectedLocum.national_id_document_url"
                  target="_blank" class="text-blue-500 hover:underline">
                  View Document
                </a>
                <span v-else class="text-gray-500">No Document</span>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="p-4 bg-gray-50 border-t border-gray-200">
            <div class="flex space-x-4">
              <button v-if="selectedLocum?.verification === 'unverified'" @click="verifyLocum"
                class="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded font-medium transition-colors"
                :disabled="verifying">
                {{ verifying ? 'Verifying...' : 'Verify' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LocumsAPI } from '@/services';
import { useToast } from 'vue-toastification';
import { setApiSystem } from '@/services/api';
import { useStore } from 'vuex';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

export default {
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  setup() {
    const toast = useToast();
    const store = useStore();
    const isAuthenticated = computed(() => store.getters.isAuthenticated);
    const router = useRouter();

    return { toast, store, isAuthenticated, router };
  },
  data() {
    return {
      locums: [],
      statuses: [],
      currentPage: 1,
      totalPages: 1,
      showPanel: false,
      selectedLocum: null,
      loading: false,
      perPage: 10,
      totalItems: 0,
      verifying: false,
    };
  },
  created() {
    if (!this.isAuthenticated) {
      this.toast.error("Please log in to the eLearning system.");
      this.router.push('/login?system=eLearning');
      return;
    }
    setApiSystem('eLearning');
    this.fetchLocums();
  },
  watch: {
    searchQuery(newQuery) {
      this.fetchLocums(newQuery);
    },
  },
  methods: {
    async fetchLocums(searchQuery = this.searchQuery, page = this.currentPage, perPage = this.perPage) {
      try {
        this.loading = true;
        const params = { search: searchQuery || undefined, page, per_page: perPage };
        const response = await LocumsAPI.getAll(params);
        this.locums = response.data.data?.locums || response.data?.locums || [];

        if (searchQuery && !response.data.data?.filtered) {
          this.locums = this.locums.filter((locum) =>
            (locum.user?.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (locum.professional_body?.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (locum.primary_role_specialty || "").toLowerCase().includes(searchQuery.toLowerCase())
          );
        }

        const meta = response.data.data?.meta || response.data?.meta;
        if (meta) {
          this.totalPages = meta.total_pages;
          this.currentPage = meta.current_page;
          this.perPage = meta.per_page;
          this.totalItems = meta.total_items;
        }
      } catch (error) {
        console.error('Fetch locums error:', error.response?.data || error.message);
        this.toast.error(`Failed to fetch locums: ${error.response?.data?.message || error.message}`);
        this.locums = [];
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.fetchLocums(this.searchQuery, this.currentPage, this.perPage);
      }
    },
    openPanel(locum) {
      this.selectedLocum = locum;
      this.showPanel = true;
    },
    closePanel() {
      this.showPanel = false;
      this.selectedLocum = null;
    },
    fetchLocumsPublic() {
      this.fetchLocums();
    },
    async verifyLocum() {
      if (!this.selectedLocum) return;
      this.verifying = true;
      try {
        await LocumsAPI.verify(this.selectedLocum.id);
        this.toast.success("Locum verified successfully.");
        this.closePanel();
        this.fetchLocums();
      } catch (error) {
        this.toast.error(`Failed to verify locum: ${error.response?.data?.message || error.message}`);
      } finally {
        this.verifying = false;
      }
    },
  },
};
</script>

<style>
.flex { display: flex; }
.transition-all { transition: width 0.3s ease-in-out; }
.bg-gray-50 { background-color: #f9fafb; }
.overflow-x-auto { overflow-x: auto; }
</style>